import {
    IPadding,
    IShadow,
    IBounds,
    NodeKind,
    IState,
    IButtonElementDataNode,
    ITextElementDataNode,
    IRadius
} from '@studio/domain';
import { ILayoutRule, GroupOrLayoutElement, isLayoutElement } from '../rules';

export class ScaleElementPropertiesRule implements ILayoutRule {
    applyRenderRule?(layoutElement: GroupOrLayoutElement, calculatedBounds: IBounds): void {
        if (isLayoutElement(layoutElement)) {
            const { element } = layoutElement;
            const xScale = calculatedBounds.width / element.width;
            const yScale = calculatedBounds.height / element.height;
            const minScale = Math.min(xScale, yScale);

            element.radius = ScaleDefault.scaleRadius(
                element.radius,
                element.width,
                element.height,
                calculatedBounds
            );

            if (element.kind === NodeKind.Text || element.kind === NodeKind.Button) {
                ScaleText.scale(element, minScale);
            }

            element.shadows = ScaleDefault.scaleShadows(element.shadows, minScale);
        }
    }
}

export class ScaleDefault {
    static scaleRadius(
        radius: IRadius,
        width: number,
        height: number,
        calculatedBounds: IBounds
    ): IRadius {
        const scaledRadius: IRadius = radius;

        Object.keys(radius).forEach(key => {
            const value = radius[key];
            const scale = value / Math.min(width, height);

            scaledRadius[key] = Math.round(
                Math.min(calculatedBounds.width, calculatedBounds.height) * scale
            );
        });

        return scaledRadius;
    }

    static scaleShadows(shadows: IShadow[] | undefined, scale: number): IShadow[] | undefined {
        if (shadows) {
            return shadows.map(s => ({
                blur: Math.ceil(s.blur * scale),
                color: s.color,
                offsetX: Math.ceil(s.offsetX * scale),
                offsetY: Math.ceil(s.offsetY * scale),
                spread: Math.ceil(s.spread * scale)
            }));
        }
    }
}

export class ScaleText {
    static scale(
        element: ITextElementDataNode | IButtonElementDataNode | IState<string | number>,
        scale: number
    ): void {
        element.padding = ScaleText.scalePadding(element.padding, scale);
        element.fontSize = ScaleText.scaleFont(element.fontSize, scale);
    }

    static scalePadding(padding: IPadding | undefined, scale: number): IPadding | undefined {
        if (padding) {
            // Note padding can only be set "together" so make all same value
            return {
                top: Math.round(padding.top * scale),
                left: Math.round(padding.left * scale),
                right: Math.round(padding.right * scale),
                bottom: Math.round(padding.bottom * scale)
            };
        }
    }

    static scaleFont(fontSize: number | undefined, scale: number): number | undefined {
        if (typeof fontSize === 'number') {
            return Math.max(Math.round(fontSize * scale), 1); // minimum fontSize 1px - STUDIO-8369
        }
    }
}
